import { CoachBotUiModule } from '@alpha-coach-bot/coach-bot/ui';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { NgIsNullPipeModule } from 'angular-pipes';

import { DataUpdatedOnModule } from '@app/modules/data-updated-on/data-updated-on.module';
import { NotificationItemComponent } from '@app/modules/notifications/components/notification-item/notification-item.component';
import { NotificationPopupComponent } from '@app/modules/notifications/components/notification-popup/notification-popup.component';
import { NotificationsComponent } from '@app/modules/notifications/components/notifications/notifications.component';
import { ChunkProgressBarComponent } from '@app/shared/components/chunk-progress-bar/chunk-progress-bar.component';
import { CustomTableComponent } from '@app/shared/components/custom-table/custom-table.component';
import { DatePickerComponent } from '@app/shared/components/date-picker/date-picker.component';
import { WarningDialogComponent } from '@app/shared/components/dialogs/warning-dialog/warning-dialog.component';
import { ErrorScreenComponent } from '@app/shared/components/error-screen/error-screen.component';
import { ErrorComponent } from '@app/shared/components/error/error.component';
import { LearningRingsModule } from '@app/shared/components/learning-rings/learning-rings.module';
import { MainHeaderComponent } from '@app/shared/components/main-header/main-header.component';
import { NotFoundComponent } from '@app/shared/components/not-found/not-found.component';
import { PillComponent } from '@app/shared/components/pill/pill.component';
import { TimerComponent } from '@app/shared/components/timer/timer.component';
import { YouAreHereModule } from '@app/shared/components/you-are-here/you-are-here.module';
import { DisableControlDirective } from '@app/shared/directives/disable-control.directive';
import FilterDirective from '@app/shared/filters/filter.directive';
import AlphaYearsFilterComponent from '@app/shared/filters/types/alpha-years.component';
import AppFilter from '@app/shared/filters/types/app.component';
import CourseFilterComponent from '@app/shared/filters/types/course.component';
import GradeLevelFilterComponent from '@app/shared/filters/types/grade-level.component';
import GuidesFilterComponent from '@app/shared/filters/types/guides.component';
import LevelComponent from '@app/shared/filters/types/level.component';
import MapFilterComponent from '@app/shared/filters/types/map.component';
import NonIntegratedAppFilterComponent from '@app/shared/filters/types/non-integrated-app.component';
import NonIntegratedCourseFilterComponent from '@app/shared/filters/types/non-integrated-course.component';
import NonMapSubjectFilterComponent from '@app/shared/filters/types/non-map-subject.component';
import ScoreFilterComponent from '@app/shared/filters/types/score.component';
import SeasonFilter from '@app/shared/filters/types/season.component';
import SessionFilterComponent from '@app/shared/filters/types/session.component';
import StudentFilterComponent from '@app/shared/filters/types/student.component';
import SubjectFilterComponent from '@app/shared/filters/types/subject.component';
import { PluralizePipe } from '@app/shared/pipes/pluralize.pipe';
import { PureFunctionPipe } from '@app/shared/pipes/pure-function.pipe';
import { SharedKendoModule } from '@app/shared/shared-kendo.module';
import { CoachBotDataAccessAuthModule } from '@coach-bot/data-access/auth';

import { SharedUtilModule } from '@coach-bot/shared/util';

import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ButtonGroupModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ErrorPageComponent } from 'app/shared/components/error-page/error-page.component';
import { TransactionListComponent } from 'app/shared/components/transaction-list/transaction-list.component';
import WeekFilterComponent from 'app/shared/filters/types/week.component';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { ImageCropperModule } from 'ngx-image-cropper';

import { AvatarComponent } from './components/avatar/avatar.component';
import { CounterComponent } from './components/counter/counter.component';
import { CoverImageUploaderComponent } from './components/cover-image-uploader/cover-image-uploader.component';
import { DailyLearningPlanComponent } from './components/daily-learning-plan/daily-learning-plan.component';
import { EmptyComponent } from './components/empty/empty.component';
import { ImageUploaderComponent } from './components/image-uploader/image-uploader.component';
import { StatusComponent } from './components/status/status.component';
import { SubHeaderComponent } from './components/sub-header/sub-header.component';
import { DisableAutofillDirective } from './directives/disable-autofill.directive';
import { MomentDatePipe } from './pipes/moment-date.pipe';
import { SingularizePipe } from './pipes/singularize.pipe';
import { SharedMatModule } from './shared-mat.module';
import { GaugeComponent } from 'app/shared/components/gauge/gauge.component';
import { LearningTooltipSectionComponent } from './components/learning-tooltip-section/learning-tooltip-section.component';
import FilterComponent from './filters/filter.component';
import { ImageUploaderService } from './services';

const SHARED_MODULES = [
  CommonModule,
  DataUpdatedOnModule,
  FlexLayoutModule,
  FormsModule,
  ImageCropperModule,
  LearningRingsModule,
  NgbTooltipModule,
  OverlayModule,
  ProgressbarModule,
  ReactiveFormsModule,
  SharedKendoModule,
  SharedMatModule,
  YouAreHereModule,
  SharedUtilModule,
  InputsModule,
  ButtonGroupModule,
  CoachBotUiModule,
  CoachBotDataAccessAuthModule,
  NgIsNullPipeModule,
];

const SHARED_COMPONENTS = [
  AvatarComponent,
  CounterComponent,
  CustomTableComponent,
  DailyLearningPlanComponent,
  DatePickerComponent,
  DisableControlDirective,
  EmptyComponent,
  ErrorComponent,
  FilterDirective,
  ImageUploaderComponent,
  StatusComponent,
  SubHeaderComponent,
  WarningDialogComponent,
  ChunkProgressBarComponent,
  CoverImageUploaderComponent,
  ErrorScreenComponent,
  NotificationPopupComponent,
  NotificationsComponent,
  NotificationItemComponent,
  MainHeaderComponent,
  NotFoundComponent,
  PillComponent,
  TransactionListComponent,
  TimerComponent,
  ScoreFilterComponent,
  GaugeComponent,
  LearningTooltipSectionComponent,
];

const FILTERS = [
  AlphaYearsFilterComponent,
  SeasonFilter,
  AppFilter,
  CourseFilterComponent,
  GuidesFilterComponent,
  GradeLevelFilterComponent,
  LevelComponent,
  MapFilterComponent,
  NonIntegratedAppFilterComponent,
  NonIntegratedCourseFilterComponent,
  NonMapSubjectFilterComponent,
  SessionFilterComponent,
  StudentFilterComponent,
  SubjectFilterComponent,
  WeekFilterComponent,
  FilterComponent,
];

const SHARED_DIRECTIVES = [DisableAutofillDirective];

const SHARED_PIPES = [MomentDatePipe, PluralizePipe, PureFunctionPipe, SingularizePipe];

const SHARED_SERVICES = [ImageUploaderService];

/**
 * The shared module is used to hold all reusable components across the app's
 * different modules. It imports and exports reusable modules to make them
 * readily available to other feature modules just by importing the shared
 * module, avoiding repetition. Since the shared module is meant to be imported
 * by all feature-modules, it shouldn't provide any service.
 */
@NgModule({
  declarations: [...FILTERS, ...SHARED_COMPONENTS, ...SHARED_PIPES, ...SHARED_DIRECTIVES, ErrorPageComponent],
  providers: [...SHARED_SERVICES],
  exports: [...SHARED_MODULES, ...SHARED_COMPONENTS, ...SHARED_PIPES, ...SHARED_DIRECTIVES],
  imports: [...SHARED_MODULES],
  entryComponents: [ImageUploaderComponent, WarningDialogComponent],
})
export class SharedModule {}
